import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, MapPin, Phone, Mail, Loader2, Check } from "lucide-react";
import { company, services, aboutMedia } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service_interest: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete the required fields.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Thank you — a partner will be in touch within 1 business day.");
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0A1128] text-white overflow-hidden"
    >
      {/* Background skyscraper */}
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${aboutMedia.skyscraper})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128] via-[#0A1128]/95 to-[#0A1128]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: heading + contact details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#CFA660] mb-6">
              — Start a Conversation
            </p>
            <h2
              data-testid="contact-title"
              className="font-['Playfair_Display'] text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium"
            >
              Tell us where you want to go
              <br />
              <span className="italic text-[#CFA660]">so we can help you get there.</span>
            </h2>
            <p className="mt-8 text-white/75 text-base md:text-lg leading-relaxed font-['Outfit'] font-light max-w-md">
              Every engagement begins with a private, no-obligation
              conversation. Share a few details and we will reach out to you
              soon.
            </p>

            <div className="mt-14 space-y-6 border-t border-white/10 pt-10">
              <div className="flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 border border-[#CFA660]/60 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#CFA660]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#CFA660] mb-1">
                    Office
                  </p>
                  <p className="text-white text-sm font-['Outfit'] leading-relaxed">
                    {company.address}
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 border border-[#CFA660]/60 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#CFA660]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#CFA660] mb-1">
                    Direct
                  </p>
                  <a
                    href={`tel:${company.phone.replace(/[^0-9+]/g, "")}`}
                    data-testid="contact-phone"
                    className="text-white text-sm font-['Outfit'] hover:text-[#CFA660] transition-colors"
                  >
                    {company.phone}
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 border border-[#CFA660]/60 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#CFA660]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#CFA660] mb-1">
                    Email
                  </p>
                  <a
                    href={`mailto:${company.email}`}
                    data-testid="contact-email"
                    className="text-white text-sm font-['Outfit'] hover:text-[#CFA660] transition-colors"
                  >
                    {company.email}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="bg-white text-[#0A1128] p-8 md:p-12">
              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="py-20 text-center flex flex-col items-center"
                >
                  <div className="w-16 h-16 border border-[#0A1128] flex items-center justify-center mb-8">
                    <Check className="w-7 h-7 text-[#CFA660]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium mb-4">
                    Message received.
                  </h3>
                  <p className="text-[#475569] max-w-md text-base font-['Outfit'] leading-relaxed">
                    A partner will personally review your note and respond within
                    one business day, in confidence.
                  </p>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => setSubmitted(false)}
                    className="mt-10 text-xs uppercase tracking-[0.25em] font-semibold border-b border-[#0A1128] pb-1 hover:text-[#172554]"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field
                      label="Full Name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      testid="contact-input-name"
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      testid="contact-input-email"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field
                      label="Phone (optional)"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      testid="contact-input-phone"
                    />
                    <Field
                      label="Company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      testid="contact-input-company"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.25em] font-semibold text-[#475569] mb-2">
                      Area of interest
                    </label>
                    <select
                      name="service_interest"
                      value={form.service_interest}
                      onChange={handleChange}
                      data-testid="contact-input-service"
                      className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 text-[#0A1128] font-['Outfit'] focus:outline-none focus:border-[#0A1128] transition-colors appearance-none cursor-pointer"
                    >
                      <option value="">Select a practice area…</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                      <option value="Other">Other / Not sure yet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.25em] font-semibold text-[#475569] mb-2">
                      How can we help? *
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-input-message"
                      placeholder="Share the situation — we treat every conversation as confidential."
                      className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 text-[#0A1128] font-['Outfit'] focus:outline-none focus:border-[#0A1128] transition-colors resize-none placeholder:text-[#94A3B8]"
                    />
                  </div>

                  <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="text-xs text-[#475569] font-['Outfit'] max-w-sm">
                      By submitting, you agree to be contacted by a Rampart
                      partner regarding your enquiry. We never share your
                      details.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit"
                      className="group inline-flex items-center justify-center gap-3 bg-[#0A1128] text-white px-8 py-4 text-xs font-semibold tracking-[0.2em] uppercase hover:bg-[#172554] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required = false, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.25em] font-semibold text-[#475569] mb-2">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 text-[#0A1128] font-['Outfit'] focus:outline-none focus:border-[#0A1128] transition-colors"
    />
  </div>
);

export default Contact;
